import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Goal, Game } from '../types';
import GoalAnalytics from '../components/GoalAnalytics';
import GoalDiagram from '../components/GoalDiagram';
import ShotPositionDiagram from '../components/ShotPositionDiagram';
import ShotPositionAnalytics from '../components/ShotPositionAnalytics';
import ShareDialog from '../components/ShareDialog';
import AITrainerButton from '../components/AITrainerButton';
import ChartModal from '../components/ChartModal';
import RiskAnalysisSection from '../components/RiskAnalysisSection';
import {
  Calendar,
  AlertCircle,
  FileDown,
  Share2,
  Lock,
  Loader,
  Brain,
  Eye,
  X,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AnalyticsPDF from '../components/AnalyticsPDF';
import { useTranslation } from '../i18n/useTranslation';
import {
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from 'recharts';
import { useRipple } from '../lib/useRipple';
import { RiskAnalysisService } from '../services/riskAnalysisService';

interface GameWithGoals extends Game {
  goals: Goal[];
}

type SelectionMode = 'all' | 'single' | 'multiple' | 'date-range';

const formatLabel = (label: string) => {
  return label
    .replace(/ Goal$/, '')
    .replace(/ Shot$/, '')
    .replace(' / ', '/');
};

const countGoalTypes = (goals: Goal[], type: 'shotType' | 'gameSituation') => {
  const counts: Record<string, number> = {};
  goals.forEach((goal) => {
    const value = goal[type];
    if (value) {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .map(([name, value]) => ({
      name: formatLabel(name),
      fullName: name,
      value,
    }))
    .sort((a, b) => b.value - a.value);
};

// Helper functions for top 5 vs all data
const getTop5Data = (data: any[]) => data.slice(0, 5);
const getAllData = (data: any[]) => data;

const countGameOutcomes = (games: Game[], t: (key: string) => string) => {
  const counts = {
    win: 0,
    loss: 0,
    'win-ot': 0,
    'loss-ot': 0,
    'win-so': 0,
    'loss-so': 0,
    noOutcome: 0,
  };

  games.forEach((game) => {
    if (!game.outcome) {
      counts.noOutcome++;
    } else {
      counts[game.outcome]++;
    }
  });

  const total = games.length;
  const gamesWithOutcome = total - counts.noOutcome;

  return [
    {
      name: t('game.outcomes.win'),
      value: counts.win,
      percentage:
        gamesWithOutcome > 0
          ? ((counts.win / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#16A34A',
    },
    {
      name: t('game.outcomes.loss'),
      value: counts.loss,
      percentage:
        gamesWithOutcome > 0
          ? ((counts.loss / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#DC2626',
    },
    {
      name: t('game.outcomes.win-ot'),
      value: counts['win-ot'],
      percentage:
        gamesWithOutcome > 0
          ? ((counts['win-ot'] / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#1B3968',
    },
    {
      name: t('game.outcomes.loss-ot'),
      value: counts['loss-ot'],
      percentage:
        gamesWithOutcome > 0
          ? ((counts['loss-ot'] / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#EA580C',
    },
    {
      name: t('game.outcomes.win-so'),
      value: counts['win-so'],
      percentage:
        gamesWithOutcome > 0
          ? ((counts['win-so'] / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#7C3AED',
    },
    {
      name: t('game.outcomes.loss-so'),
      value: counts['loss-so'],
      percentage:
        gamesWithOutcome > 0
          ? ((counts['loss-so'] / gamesWithOutcome) * 100).toFixed(1)
          : '0',
      color: '#DB2777',
    },
  ].filter((item) => item.value > 0);
};

const calculateWinPercentage = (games: Game[]) => {
  const gamesWithOutcome = games.filter((game) => game.outcome);
  if (gamesWithOutcome.length === 0) return null;

  const wins = gamesWithOutcome.filter(
    (game) =>
      game.outcome === 'win' ||
      game.outcome === 'win-ot' ||
      game.outcome === 'win-so'
  ).length;

  return ((wins / gamesWithOutcome.length) * 100).toFixed(1);
};

export default function Analytics() {
  const { t } = useTranslation();
  const user = useStore((state) => state.user);
  const getUsageInfo = useStore((state) => state.getUsageInfo);
  const setShowUpgradeModal = useStore((state) => state.setShowUpgradeModal);
  const [games, setGames] = useState<GameWithGoals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('all');
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const [pdfReady, setPdfReady] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showShotTypeModal, setShowShotTypeModal] = useState(false);
  const [showGameSituationModal, setShowGameSituationModal] = useState(false);
  const [showImportantNote, setShowImportantNote] = useState(true);
  const { createRipple } = useRipple();

  const [activeShotTypeIndex, setActiveShotTypeIndex] = useState<number>(0);
  const [activeGameSituationIndex, setActiveGameSituationIndex] = useState<number>(0);

  // Get usage info from centralized store
  const usageInfo = getUsageInfo('analysis');

  const handleUpgrade = async () => {
    if (!user) return;
    setShowUpgradeModal(true);
  };

  const getSelectedGameIds = () => {
    switch (selectionMode) {
      case 'all':
        return games.map((game) => game.id);
      case 'single':
      case 'multiple':
        return Array.from(selectedGames);
      case 'date-range': {
        return games
          .filter((game) => {
            const gameDate = new Date(game.date);
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            return gameDate >= startDate && gameDate <= endDate;
          })
          .map((game) => game.id);
      }
      default:
        return [];
    }
  };

  useEffect(() => {
    const fetchGamesAndGoals = async () => {
      if (!user) return;

      try {
        const gamesQuery = query(
          collection(db, 'games'),
          where('userId', '==', user.uid)
        );

        const gamesSnapshot = await getDocs(gamesQuery);
        const gamesData = gamesSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          date: new Date(doc.data().date),
          createdAt: new Date(doc.data().createdAt),
          goals: [],
        })) as unknown as GameWithGoals[];

        gamesData.sort((a, b) => b.date.getTime() - a.date.getTime());

        if (gamesData.length > 0) {
          const latestGame = gamesData[0];
          const earliestGame = gamesData[gamesData.length - 1];
          setDateRange({
            start: earliestGame.date.toISOString().split('T')[0],
            end: latestGame.date.toISOString().split('T')[0],
          });
        }

        const goalsPromises = gamesData.map(async (game) => {
          const goalsQuery = query(
            collection(db, 'goals'),
            where('gameId', '==', game.id)
          );
          const goalsSnapshot = await getDocs(goalsQuery);
          return goalsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            createdAt: new Date(doc.data().createdAt),
          })) as Goal[];
        });

        const allGoalsArrays = await Promise.all(goalsPromises);

        const gamesWithGoals = gamesData.map((game, index) => ({
          ...game,
          goals: allGoalsArrays[index],
        }));

        const flattenedGoals = allGoalsArrays.flat();

        setGames(gamesWithGoals);
        setAllGoals(flattenedGoals);
        setPdfReady(true);
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError(t('messages.loadingError'));
      } finally {
        setLoading(false);
      }
    };

    fetchGamesAndGoals();
  }, [user]);

  const handleGameSelection = (gameId: string) => {
    setSelectedGames((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(gameId)) {
        newSelection.delete(gameId);
      } else {
        newSelection.add(gameId);
      }
      return newSelection;
    });
  };

  const getDisplayedGoals = () => {
    switch (selectionMode) {
      case 'all':
        return allGoals;
      case 'single': {
        const selectedGameId = Array.from(selectedGames)[0];
        return games.find((game) => game.id === selectedGameId)?.goals || [];
      }
      case 'multiple':
        return games
          .filter((game) => selectedGames.has(game.id))
          .flatMap((game) => game.goals);
      case 'date-range': {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        endDate.setHours(23, 59, 59, 999);
        return games
          .filter((game) => {
            const gameDate = new Date(game.date);
            return gameDate >= startDate && gameDate <= endDate;
          })
          .flatMap((game) => game.goals);
      }
    }
  };

  const displayedGoals = getDisplayedGoals();
  const outcomeStats = countGameOutcomes(games, t);
  
  // Safely analyze risks with error handling
  let riskAnalysis = null;
  try {
    riskAnalysis = RiskAnalysisService.analyzeRisks(displayedGoals);
  } catch (error) {
    console.error('Failed to analyze risks:', error);
    // Service already handles errors internally, but this is extra safety
  }

  // Reset active indices if data changes and index is out of bounds
  useEffect(() => {
    const shotTypeData = countGoalTypes(displayedGoals, 'shotType');
    if (shotTypeData.length === 0) {
      setActiveShotTypeIndex(0);
    } else if (activeShotTypeIndex >= shotTypeData.length) {
      setActiveShotTypeIndex(0);
    }
    const gameSituationData = countGoalTypes(displayedGoals, 'gameSituation');
    if (gameSituationData.length === 0) {
      setActiveGameSituationIndex(0);
    } else if (activeGameSituationIndex >= gameSituationData.length) {
      setActiveGameSituationIndex(0);
    }
  }, [outcomeStats, displayedGoals]);

  const handleSelectionModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);
    setSelectedGames(new Set());
  };

  const selectedGamesData =
    selectionMode === 'date-range'
      ? games.filter((game) => {
          const gameDate = new Date(game.date);
          const startDate = new Date(dateRange.start);
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          return gameDate >= startDate && gameDate <= endDate;
        })
      : games.filter((game) => selectedGames.has(game.id));

  const calculateSavePercentage = (gamesData: GameWithGoals[]) => {
    const totalSaves = gamesData.reduce(
      (acc, game) => acc + (game.saves || 0),
      0
    );
    const totalShots = gamesData.reduce(
      (acc, game) => acc + (game.shotsFaced || 0),
      0
    );
    return totalShots > 0 ? ((totalSaves / totalShots) * 100).toFixed(1) : '—';
  };

  const calculateAverageGoalsPerGame = (gamesData: GameWithGoals[]) => {
    if (gamesData.length === 0) return '0.00';
    const totalGoals = gamesData.reduce(
      (acc, game) => acc + game.goals.length,
      0
    );
    return (totalGoals / gamesData.length).toFixed(2);
  };

  const getPdfFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    switch (selectionMode) {
      case 'all':
        return `all-games-analytics-${timestamp}.pdf`;
      case 'single': {
        const selectedGame = games.find(
          (game) => game.id === Array.from(selectedGames)[0]
        );
        return selectedGame
          ? `game-analytics-${selectedGame.opponent}-${
              selectedGame.date.toISOString().split('T')[0]
            }.pdf`
          : `game-analytics-${timestamp}.pdf`;
      }
      case 'multiple':
        return `selected-games-analytics-${timestamp}.pdf`;
      case 'date-range':
        return `date-range-analytics-${dateRange.start}-to-${dateRange.end}.pdf`;
      default:
        return `goalie-analytics-${timestamp}.pdf`;
    }
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    value,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    name: string;
    value: number;
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill='#ffffff'
        textAnchor='middle'
        dominantBaseline='central'
        fontSize={11}
        fontWeight={600}
      >
        {`${name}`}
        <tspan x={x} dy={12} fontSize={10} fontWeight={400}>
          {`(${value})`}
        </tspan>
      </text>
    );
  };

  const renderBarLabel = (props: any) => {
    const { x, y, width, height, name, value } = props;
    
    if (!name || value === undefined || width < 30 || height < 20) {
      return <text />;
    }
    
    const labelX = x + width / 2;
    const labelY = y + height / 2;
    const fullText = `${name} (${value})`;
    const charWidth = 7; // Approximate character width
    const lineHeight = 14; // Line height for text
    
    // If bar is wide enough for full text on one line, show it
    if (width >= fullText.length * charWidth + 10) {
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={12}
          fontWeight={600}
        >
          {fullText}
        </text>
      );
    }
    
    // Calculate how many characters can fit per line
    const maxCharsPerLine = Math.floor((width - 10) / charWidth);
    
    // If we can fit at least 4 characters per line and have enough height for 2 lines
    if (maxCharsPerLine >= 4 && height >= 28) {
      // Split into name and value parts
      const words = name.split(' ');
      const valueText = `(${value})`;
      
      // Try to fit name on first line, value on second
      if (name.length <= maxCharsPerLine && valueText.length <= maxCharsPerLine) {
        return (
          <text
            x={labelX}
            y={labelY}
            fill='#ffffff'
            textAnchor='middle'
            dominantBaseline='central'
            fontSize={11}
            fontWeight={600}
          >
            <tspan x={labelX} dy={-lineHeight / 2}>
              {name}
            </tspan>
            <tspan x={labelX} dy={lineHeight}>
              {valueText}
            </tspan>
          </text>
        );
      }
      
      // If name is too long, try to wrap it intelligently
      let line1 = '';
      let line2 = '';
      
      if (words.length > 1) {
        // Try to split by words
        line1 = words[0];
        line2 = words.slice(1).join(' ') + ` (${value})`;
        
        // If second line is too long, truncate and add value
        if (line2.length > maxCharsPerLine) {
          const remainingSpace = maxCharsPerLine - ` (${value})`.length - 3; // 3 for "..."
          if (remainingSpace > 0) {
            line2 = words.slice(1).join(' ').substring(0, remainingSpace) + `... (${value})`;
          } else {
            line2 = `(${value})`;
          }
        }
      } else {
        // Single word - split the word if necessary
        if (name.length <= maxCharsPerLine) {
          line1 = name;
          line2 = `(${value})`;
        } else {
          const splitPoint = Math.max(3, maxCharsPerLine - 3);
          line1 = name.substring(0, splitPoint) + '...';
          line2 = `(${value})`;
        }
      }
      
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={10}
          fontWeight={600}
        >
          <tspan x={labelX} dy={-lineHeight / 2}>
            {line1}
          </tspan>
          <tspan x={labelX} dy={lineHeight}>
            {line2}
          </tspan>
        </text>
      );
    }
    
    // For very narrow bars, just show the value if it fits
    if (width >= value.toString().length * charWidth + 5) {
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={10}
          fontWeight={600}
        >
          {value}
        </text>
      );
    }
    
    // Bar too narrow for any meaningful text
    return <text />;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen px-4'>
        <div className='glass-card max-w-md w-full p-8 text-center animate-float'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4'></div>
          <p className='text-slate-600 font-medium'>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center min-h-screen px-4'>
        <div className='glass-card max-w-2xl w-full p-6 border-red-200/50 bg-red-50/80'>
          <div className='text-red-700 font-medium text-center'>{error}</div>
        </div>
      </div>
    );
  }

  const winPercentage = calculateWinPercentage(games);

  return (
    <div className='max-w-7xl mx-auto px-4 py-8 space-y-8'>
      {/* Show upgrade prompt if user has reached limit */}
      {usageInfo && !usageInfo.allowed && (
        <div className='glass-card p-6 border-amber-200/30 bg-amber-50/40'>
          <div className='flex items-start'>
            <Lock className='h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0' />
            <div className='flex-1'>
              <h3 className='text-sm font-semibold text-amber-800 mb-1'>
                AI Analysis Limit Reached
              </h3>
              <p className='text-sm text-amber-700 mb-4'>
                You've used {usageInfo.usage?.used || 0} of{' '}
                {usageInfo.usage?.limit || 0} free AI analyses. Upgrade to get
                unlimited access.
              </p>
              <button
                onClick={(e) => {
                  createRipple(e);
                  handleUpgrade();
                }}
                disabled={loading}
                className='btn-mui-primary btn-ripple'
              >
                {loading ? (
                  <Loader className='h-5 w-5 animate-spin' />
                ) : (
                  <Brain className='h-5 w-5' />
                )}
                <span>
                  {loading ? t('common.processing') : t('analytics.upgradeForAIAnalysis')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className='glass-card p-6'>
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold text-primary-600 mb-2 italic'>
              {t('analytics.title')}
            </h1>
            <p className='text-muted'>
              {t('analytics.subtitle')}
            </p>
          </div>

          <div className='flex flex-col md:flex-row md:items-center gap-4'>
            {/* AI Trainer button - appears first on mobile */}
            {!(usageInfo && !usageInfo.allowed) && (
              <div className='flex justify-center md:justify-start'>
                <AITrainerButton
                  gameIds={getSelectedGameIds()}
                  games={selectionMode === 'all' ? games : selectedGamesData}
                  goals={displayedGoals}
                  variant='primary'
                />
              </div>
            )}
            {/* Export and Share buttons - appear below AI Trainer on mobile, inline on desktop */}
            {pdfReady && (
              <div className='flex items-center gap-3 justify-start'>
                <PDFDownloadLink
                  document={
                    <AnalyticsPDF
                      games={
                        selectionMode === 'all' ? games : selectedGamesData
                      }
                      goals={displayedGoals}
                      dateRange={
                        selectionMode === 'date-range' ? dateRange : undefined
                      }
                      selectionMode={selectionMode}
                    />
                  }
                  fileName={getPdfFilename()}
                  className='btn-mui-secondary btn-ripple group'
                  onClick={createRipple}
                >
                  <FileDown className='h-5 w-5' />
                  <span>{t('analytics.exportPDF')}</span>
                </PDFDownloadLink>
                <button
                  onClick={(e) => {
                    createRipple(e);
                    setShowShareDialog(true);
                  }}
                  className='btn-mui-secondary btn-ripple group'
                >
                  <Share2 className='h-5 w-5' />
                  <span className='hidden sm:inline'>{t('common.share')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className='glass-card p-2'>
        <div className='grid grid-cols-2 sm:flex rounded-lg overflow-hidden gap-1 sm:gap-0'>
          <button
            onClick={(e) => {
              createRipple(e);
              handleSelectionModeChange('all');
            }}
            className={`btn-ripple px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all duration-300 flex-1 rounded-md sm:rounded-none ${
              selectionMode === 'all'
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                : 'text-slate-700 hover:text-primary-600 hover:bg-primary-50/50'
            }`}
          >
            {t('analytics.all')}
          </button>
          <button
            onClick={(e) => {
              createRipple(e);
              handleSelectionModeChange('single');
            }}
            className={`btn-ripple px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all duration-300 flex-1 rounded-md sm:rounded-none ${
              selectionMode === 'single'
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                : 'text-slate-700 hover:text-primary-600 hover:bg-primary-50/50'
            }`}
          >
            {t('analytics.single')}
          </button>
          <button
            onClick={(e) => {
              createRipple(e);
              handleSelectionModeChange('multiple');
            }}
            className={`btn-ripple px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all duration-300 flex-1 rounded-md sm:rounded-none ${
              selectionMode === 'multiple'
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                : 'text-slate-700 hover:text-primary-600 hover:bg-primary-50/50'
            }`}
          >
            {t('analytics.multiple')}
          </button>
          <button
            onClick={(e) => {
              createRipple(e);
              handleSelectionModeChange('date-range');
            }}
            className={`btn-ripple px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium transition-all duration-300 flex-1 rounded-md sm:rounded-none ${
              selectionMode === 'date-range'
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg'
                : 'text-slate-700 hover:text-primary-600 hover:bg-primary-50/50'
            }`}
          >
            <span className='hidden sm:inline'>{t('analytics.dateRange')}</span>
            <span className='sm:hidden'>{t('analytics.range')}</span>
          </button>
        </div>
      </div>

      {selectionMode === 'date-range' && (
        <div className='glass-card p-4 sm:p-6'>
          <div className='space-y-4 sm:space-y-0 sm:flex sm:items-end sm:gap-4'>
            <div className='flex-1 min-w-0'>
              <label
                htmlFor='start-date'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                {t('analytics.from')}
              </label>
              <div className='relative'>
                <input
                  type='date'
                  id='start-date'
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      start: e.target.value,
                    }))
                  }
                  className='pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968] w-full text-sm'
                />
                <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              </div>
            </div>
            <div className='flex-1 min-w-0'>
              <label
                htmlFor='end-date'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                {t('analytics.to')}
              </label>
              <div className='relative'>
                <input
                  type='date'
                  id='end-date'
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                  className='pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968] w-full text-sm'
                />
                <Calendar className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
              </div>
            </div>
          </div>
          {dateRange.start && dateRange.end && (
            <div className='mt-3 p-2 bg-primary-50/50 rounded-md'>
              <p className='text-xs text-primary-700 break-words'>
                <span className='font-medium'>{t('analytics.range')}:</span>{' '}
                <span className='block sm:inline'>
                  {new Date(dateRange.start).toLocaleDateString()}
                </span>{' '}
                <span className='hidden sm:inline'>{t('analytics.to')}</span>
                <span className='block sm:hidden text-center my-1'>↓</span>
                <span className='block sm:inline'>
                  {new Date(dateRange.end).toLocaleDateString()}
                </span>
              </p>
            </div>
          )}
        </div>
      )}

      {(selectionMode === 'single' || selectionMode === 'multiple') && (
        <div className='mb-8'>
          <div className='glass-card p-6'>
            <h2 className='text-xl font-bold text-heading mb-6'>
              {selectionMode === 'single' ? t('analytics.selectGamesToAnalyze') : t('analytics.selectGames')}
            </h2>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {games.map((game) => (
                <button
                  key={game.id}
                  onClick={() => {
                    if (selectionMode === 'single') {
                      setSelectedGames(new Set([game.id]));
                    } else {
                      handleGameSelection(game.id);
                    }
                  }}
                  className={`glass-card p-5 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group ${
                    selectedGames.has(game.id)
                      ? 'ring-2 ring-[#1b3968] bg-gradient-to-br from-[#1b3968]/10 to-[#1b3968]/5 shadow-lg'
                      : 'hover:ring-2 hover:ring-[#1b3968]/30 hover:bg-gradient-to-br hover:from-[#1b3968]/5 hover:to-transparent'
                  }`}
                >
                  <div className='flex justify-between items-center'>
                    <div className='text-left flex-1'>
                      <p className='font-semibold text-heading text-lg mb-1 group-hover:text-[#1b3968] transition-colors duration-300'>
                        {game.opponent}
                      </p>
                      <p className='text-sm text-muted font-medium'>
                        {game.date.toLocaleDateString()}
                      </p>
                      {game.outcome && (
                        <div className='mt-2'>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              game.outcome.includes('win')
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {game.outcome.charAt(0).toUpperCase() +
                              game.outcome.slice(1).replace('-', ' ')}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className='ml-4 flex flex-col items-center'>
                      {selectedGames.has(game.id) ? (
                        <div className='h-8 w-8 bg-gradient-to-br from-[#1b3968] to-[#15304f] rounded-full flex items-center justify-center shadow-lg animate-pulse-glow'>
                          <svg
                            className='h-5 w-5 text-white'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M5 13l4 4L19 7'
                            />
                          </svg>
                        </div>
                      ) : (
                        <div className='h-8 w-8 border-2 border-gray-300 rounded-full flex items-center justify-center group-hover:border-[#1b3968] group-hover:shadow-md transition-all duration-300'>
                          <div className='h-3 w-3 rounded-full bg-transparent group-hover:bg-[#1b3968]/20 transition-all duration-300'></div>
                        </div>
                      )}
                      <div className='mt-2 text-xs text-muted font-medium'>
                        {game.goals?.length || 0} goals
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showImportantNote && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg p-4 my-6 flex items-start'>
          <AlertCircle className='h-5 w-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0' />
          <div className='flex-1'>
            <h3 className='font-medium text-amber-800 mb-1 text-sm'>
              {t('analytics.importantNote')}
            </h3>
            <p className='text-amber-700 text-sm'>
              {t('analytics.accurateStatisticsNote')}
            </p>
          </div>
          <button
            onClick={() => setShowImportantNote(false)}
            className='ml-3 text-amber-500 hover:text-amber-700 transition-colors duration-200'
          >
            <X className='h-4 w-4' />
          </button>
        </div>
      )}

      {/* Risk Analysis Section */}
      <RiskAnalysisSection riskAnalysis={riskAnalysis} />

      {/* Metrics Cards */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-6'>
        <div className='metric-card group hover:scale-105 transition-transform duration-300'>
          <div className='text-3xl font-bold text-heading mb-2'>
            {displayedGoals.length}
          </div>
          <h3 className='text-sm font-semibold text-subheading mb-1'>
            {t('analytics.totalGoalsAgainst')}
          </h3>
          <p className='text-xs text-muted'>
            {selectionMode === 'all'
              ? t('analytics.acrossGames', { count: games.length.toString() })
              : t('analytics.acrossSelectedGames', { 
                  count: selectedGamesData.length.toString(),
                  plural: selectedGamesData.length > 1 ? 's' : ''
                })}
          </p>
        </div>
        <div className='metric-card group hover:scale-105 transition-transform duration-300'>
          <div className='text-3xl font-bold text-heading mb-2'>
            {selectionMode === 'all'
              ? calculateAverageGoalsPerGame(games)
              : calculateAverageGoalsPerGame(selectedGamesData)}
          </div>
          <h3 className='text-sm font-semibold text-subheading mb-1'>
            {t('analytics.averageGoalsPerGame')}
          </h3>
          <p className='text-xs text-muted'>{t('analytics.basedOnSelectedData')}</p>
        </div>
        <div className='metric-card group hover:scale-105 transition-transform duration-300'>
          <div className='text-3xl font-bold text-heading mb-2'>
            {selectionMode === 'all'
              ? `${calculateSavePercentage(games)}%`
              : `${calculateSavePercentage(selectedGamesData)}%`}
          </div>
          <h3 className='text-sm font-semibold text-subheading mb-1'>
            {t('analytics.savePercentage')}
          </h3>
          <p className='text-xs text-muted'>{t('analytics.overallSavePercentage')}</p>
        </div>
        {winPercentage !== null && (
          <div className='metric-card group hover:scale-105 transition-transform duration-300'>
            <div className='text-3xl font-bold text-heading mb-2'>
              {winPercentage}%
            </div>
            <h3 className='text-sm font-semibold text-subheading mb-1'>
              {t('analytics.winPercentage')}
            </h3>
            <p className='text-xs text-muted'>{t('analytics.basedOnGamesWithOutcomes')}</p>
          </div>
        )}
      </div>

      {outcomeStats.length > 0 && (
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          <div className='chart-container relative'>
            <h3 className='text-lg font-semibold text-heading mb-4'>
              {t('analytics.gameOutcomesBreakdown')}
            </h3>
            <div className='h-[300px]'>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <defs>
                    <radialGradient id='winGradient' cx='50%' cy='50%' r='50%'>
                      <stop offset='0%' stopColor='#22C55E' />
                      <stop offset='70%' stopColor='#16A34A' />
                      <stop offset='100%' stopColor='#15803D' />
                    </radialGradient>
                    <radialGradient id='lossGradient' cx='50%' cy='50%' r='50%'>
                      <stop offset='0%' stopColor='#F87171' />
                      <stop offset='70%' stopColor='#DC2626' />
                      <stop offset='100%' stopColor='#B91C1C' />
                    </radialGradient>
                    <radialGradient
                      id='winOTGradient'
                      cx='50%'
                      cy='50%'
                      r='50%'
                    >
                      <stop offset='0%' stopColor='#60A5FA' />
                      <stop offset='70%' stopColor='#1B3968' />
                      <stop offset='100%' stopColor='#1D4ED8' />
                    </radialGradient>
                    <radialGradient
                      id='lossOTGradient'
                      cx='50%'
                      cy='50%'
                      r='50%'
                    >
                      <stop offset='0%' stopColor='#FB923C' />
                      <stop offset='70%' stopColor='#EA580C' />
                      <stop offset='100%' stopColor='#C2410C' />
                    </radialGradient>
                    <radialGradient
                      id='winSOGradient'
                      cx='50%'
                      cy='50%'
                      r='50%'
                    >
                      <stop offset='0%' stopColor='#A78BFA' />
                      <stop offset='70%' stopColor='#7C3AED' />
                      <stop offset='100%' stopColor='#6D28D9' />
                    </radialGradient>
                    <radialGradient
                      id='lossSOGradient'
                      cx='50%'
                      cy='50%'
                      r='50%'
                    >
                      <stop offset='0%' stopColor='#F472B6' />
                      <stop offset='70%' stopColor='#DB2777' />
                      <stop offset='100%' stopColor='#BE185D' />
                    </radialGradient>
                  </defs>
                  <Pie
                    data={outcomeStats}
                    cx='50%'
                    cy='50%'
                    labelLine={true}
                    label={renderCustomizedLabel}
                    outerRadius={90}
                    innerRadius={20}
                    fill='#8884d8'
                    dataKey='value'
                    stroke='rgba(255, 255, 255, 0.8)'
                    strokeWidth={2}
                  >
                    {outcomeStats.map((entry, index) => {
                      const getGradientId = (name: string) => {
                        switch (name) {
                          case 'Win':
                            return 'url(#winGradient)';
                          case 'Loss':
                            return 'url(#lossGradient)';
                          case 'Win (OT)':
                            return 'url(#winOTGradient)';
                          case 'Loss (OT)':
                            return 'url(#lossOTGradient)';
                          case 'Win (SO)':
                            return 'url(#winSOGradient)';
                          case 'Loss (SO)':
                            return 'url(#lossSOGradient)';
                          default:
                            return entry.color;
                        }
                      };
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={getGradientId(entry.name)}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className='chart-container relative'>
            <h3 className='text-lg font-semibold text-heading mb-4'>
              {t('analytics.top5ShotTypesDistribution')}
            </h3>
            <div className='h-[300px]'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  data={getTop5Data(countGoalTypes(displayedGoals, 'shotType'))}
                  layout='vertical'
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id='shotTypeGradient'
                      x1='0'
                      y1='0'
                      x2='1'
                      y2='0'
                    >
                      <stop offset='0%' stopColor='#3B82F6' />
                      <stop offset='50%' stopColor='#1D4ED8' />
                      <stop offset='100%' stopColor='#1E40AF' />
                    </linearGradient>
                  </defs>
                  <XAxis
                    type='number'
                    tickCount={
                      Math.max(
                        ...countGoalTypes(displayedGoals, 'shotType').map(
                          (item) => item.value
                        )
                      ) + 1
                    }
                    domain={[0, 'dataMax']}
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                    axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                    tickLine={{ stroke: '#CBD5E1' }}
                  />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={20}
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                    formatter={(value: number, name: string, item: unknown) => {
                      const payload = item as {
                        payload: { fullName: string };
                      };
                      return [value, payload.payload.fullName];
                    }}
                  />
                  <Bar
                    dataKey='value'
                    fill='url(#shotTypeGradient)'
                    radius={[0, 6, 6, 0]}
                    stroke='rgba(255, 255, 255, 0.5)'
                    strokeWidth={1}
                    label={renderBarLabel}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* See All Button */}
            <div className='mt-4 flex justify-center'>
              <button
                onClick={(e) => {
                  createRipple(e);
                  setShowShotTypeModal(true);
                }}
                className='btn-mui-secondary btn-ripple group flex items-center gap-2'
              >
                <Eye className='h-4 w-4' />
                <span>{t('analytics.seeAllShotTypes')}</span>
              </button>
            </div>
          </div>

          <div className='chart-container relative'>
            <h3 className='text-lg font-semibold text-heading mb-4'>
              {t('analytics.top5GameSituationsDistribution')}
            </h3>
            <div className='h-[300px]'>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  data={getTop5Data(countGoalTypes(displayedGoals, 'gameSituation'))}
                  layout='vertical'
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id='gameSituationGradient'
                      x1='0'
                      y1='0'
                      x2='1'
                      y2='0'
                    >
                      <stop offset='0%' stopColor='#10B981' />
                      <stop offset='50%' stopColor='#059669' />
                      <stop offset='100%' stopColor='#047857' />
                    </linearGradient>
                  </defs>
                  <XAxis
                    type='number'
                    tickCount={
                      Math.max(
                        ...countGoalTypes(displayedGoals, 'gameSituation').map(
                          (item) => item.value
                        )
                      ) + 1
                    }
                    domain={[0, 'dataMax']}
                    allowDecimals={false}
                    tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                    axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                    tickLine={{ stroke: '#CBD5E1' }}
                  />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={20}
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(12px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                    formatter={(value: number, name: string, item: unknown) => {
                      const payload = item as {
                        payload: { fullName: string };
                      };
                      return [value, payload.payload.fullName];
                    }}
                  />
                  <Bar
                    dataKey='value'
                    fill='url(#gameSituationGradient)'
                    radius={[0, 6, 6, 0]}
                    stroke='rgba(255, 255, 255, 0.5)'
                    strokeWidth={1}
                    label={renderBarLabel}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* See All Button */}
            <div className='mt-4 flex justify-center'>
              <button
                onClick={(e) => {
                  createRipple(e);
                  setShowGameSituationModal(true);
                }}
                className='btn-mui-secondary btn-ripple group flex items-center gap-2'
              >
                <Eye className='h-4 w-4' />
                <span>{t('analytics.seeAllGameSituations')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {(selectionMode === 'all' ||
        selectedGames.size > 0 ||
        selectionMode === 'date-range') && (
        <>
          <div className='glass-card p-6'>
            <h2 className='text-2xl font-bold text-gradient'>
              {t('analytics.analyticsResults')}
            </h2>
            <p className='text-muted mt-2'>
              {t('analytics.detailedVisualization')}
            </p>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='chart-container'>
              <h2 className='text-xl font-semibold text-heading mb-6'>
                {t('analytics.goalDistributionHeatmap')}
              </h2>
              <div className='flex justify-center'>
                <GoalAnalytics
                  goals={displayedGoals}
                  width={Math.min(window.innerWidth - 40, 500)}
                  height={Math.min(window.innerWidth - 40, 375)}
                />
              </div>
              <p className='mt-4 text-sm text-gray-600'>
                {t('analytics.heatmapDescription')}
                Darker red indicates a higher percentage of goals.
              </p>
            </div>

            <div className='chart-container'>
              <h2 className='text-xl font-semibold text-heading mb-6'>
                {t('analytics.goalLocations')}
              </h2>
              <div className='flex justify-center'>
                <GoalDiagram
                  onPositionSelect={() => {}}
                  goals={displayedGoals}
                  width={Math.min(window.innerWidth - 40, 500)}
                  height={Math.min(window.innerWidth - 40, 375)}
                  readOnly={true}
                  hideNumbers={selectionMode === 'all'}
                />
              </div>
              <p className='mt-4 text-sm text-gray-600'>
                {t('analytics.goalPointsExplanation')}{' '}
                {selectionMode !== 'all' &&
                  'Numbers indicate the sequence of goals.'}
              </p>
            </div>

            <div className='chart-container'>
              <h2 className='text-xl font-semibold text-heading mb-6'>
                {t('analytics.shotDistributionHeatmap')}
              </h2>
              <div className='flex justify-center'>
                <ShotPositionAnalytics
                  goals={displayedGoals}
                  width={Math.min(window.innerWidth - 40, 500)}
                  height={Math.min(window.innerWidth - 40, 375)}
                />
              </div>
              <p className='mt-4 text-sm text-gray-600'>
                {t('analytics.shotHeatmapDescription')}
              </p>
            </div>

            <div className='chart-container'>
              <h2 className='text-xl font-semibold text-heading mb-6'>
                {t('analytics.shotLocations')}
              </h2>
              <div className='flex justify-center'>
                <ShotPositionDiagram
                  onPositionSelect={() => {}}
                  goals={displayedGoals}
                  width={Math.min(window.innerWidth - 40, 500)}
                  height={Math.min(window.innerWidth - 40, 375)}
                  readOnly={true}
                  hideNumbers={selectionMode === 'all'}
                />
              </div>
              <p className='mt-4 text-sm text-gray-600'>
                {t('analytics.shotPointsDescription')}{' '}
                {selectionMode !== 'all' &&
                  'Numbers indicate the sequence of shots.'}
              </p>
            </div>
          </div>
        </>
      )}

      {showShareDialog && (
        <ShareDialog
          gameIds={getSelectedGameIds()}
          onClose={() => setShowShareDialog(false)}
          isOpen={showShareDialog}
        />
      )}

      {/* Chart Modals */}
      <ChartModal
        isOpen={showShotTypeModal}
        onClose={() => setShowShotTypeModal(false)}
        data={getAllData(countGoalTypes(displayedGoals, 'shotType'))}
        title={t('analytics.allShotTypesDistribution')}
        chartType="shotType"
      />

      <ChartModal
        isOpen={showGameSituationModal}
        onClose={() => setShowGameSituationModal(false)}
        data={getAllData(countGoalTypes(displayedGoals, 'gameSituation'))}
        title={t('analytics.allGameSituationsDistribution')}
        chartType="gameSituation"
      />
    </div>
  );
}